import { InputHTMLAttributes, ReactNode, Ref, useCallback, useRef } from 'react';

import { FormattedInput } from '@flipster/react/design-utils/formatted-input';
import { useMergeRef } from '@flipster/react/misc/ref/use-merge-ref';
import { useControllableState } from '@flipster/react/misc/state/use-controllable-state';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import Num, { inputFormatter, numberFormatter } from '@flipster/utils/num';

export interface NumberInputWithSuffixProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  ref?: Ref<HTMLInputElement>;
  className?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  precision: number;
  max?: string;
  suffix?: ReactNode;
}

// field-sizing 속성을 이용하면 코드를 훨씬 단순하게 만들 수 있지만
// 브라우저 지원범위가 매우 협소해서 안보이는 div로 위치를 잡아주는 방식으로 구현함.
// - https://developer.chrome.com/docs/css-ui/css-field-sizing
// - https://caniuse.com/?search=field-sizing
export const NumberInputWithSuffix = ({
  ref,
  className,
  precision,
  max,
  onChange,
  value,
  defaultValue,
  suffix,
  ...props
}: NumberInputWithSuffixProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRef(inputRef, ref);
  const [innerValue, setInnerValue] = useControllableState({
    value,
    defaultValue,
    onChange(nextValue) {
      if (checkOverMax(nextValue, max)) {
        onChange?.(numberFormatter(max, { withComma: false, precision }));
        inputRef.current?.setSelectionRange(-1, -1);
        return;
      }
      onChange?.(nextValue);
    },
  });
  const formatter = useCallback((val: string) => inputFormatter(val, { withComma: true, precision }), [precision]);
  const deformatter = useCallback((val: string) => val.replace(/[^\d.]/g, ''), []);
  const formattedValue = formatter(innerValue || '');

  return (
    <div
      className={cx(
        className,
        hstack({
          h: 'inherit',
          pos: 'relative',
          flexBasis: 0,
          flexGrow: 1,
          '&:focus-within .clear': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }),
      )}
    >
      <FormattedInput
        ref={mergedRef}
        className={css({
          font: 'inherit',
          pos: 'absolute',
          p: 'inherit',
          pr: 0,
          minW: 0,
          w: '100%',
          h: '100%',
          color: 'inherit',
          zIndex: 1,
        })}
        value={innerValue}
        onChange={setInnerValue}
        formatter={formatter}
        deformatter={deformatter}
        maxLength={12}
        inputMode='numeric'
        autoCapitalize='off'
        autoComplete='off'
        autoCorrect='off'
        spellCheck='false'
        data-lpignore='true'
        {...props}
      />
      <div
        className={css({
          font: 'inherit',
          letterSpacing: 'normal',
          opacity: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        })}
      >
        {formattedValue}
      </div>
      {suffix}
    </div>
  );
};

function checkOverMax(val?: string, max?: string) {
  return val != null && max != null && Num(val).isGreaterThan(max);
}
